import { plainDate, ageFrom } from './format';
import type { MetaResponse, SubmissionRow } from '../api/client';

/**
 * View helpers ported from the demo's presenter methods.
 *
 * These stayed on the frontend deliberately: they are pure presentation, and
 * the labels they resolve come from `/meta` so a study or topic added on the
 * API shows up here without a change. `who` and the summary line are computed
 * server-side too (they feed search and the list projection) — these are for
 * fields the row already carries.
 */

type AnyData = Record<string, any>;

export interface Lookup {
  studyLabel: (key: string | undefined) => string;
  studyShort: (key: string | undefined) => string;
  topicLabel: (key: string | undefined) => string;
  /** Canonical CRM key → label, for the registry forms' `fields` objects. */
  fieldLabel: (key: string) => string;
  /** A stored enum value's human reading; the value itself off-vocabulary. */
  valueLabel: (key: string, value: string) => string;
  /**
   * Where a key sits in `/meta`'s label map — the one ordered list of CRM keys
   * the dashboard has. MySQL's JSON column does not keep key order, so a
   * form's answers come back shuffled and this puts them back in form order.
   */
  fieldRank: (key: string) => number;
}

/**
 * Enum answers that only made sense next to the form's other options. "Both"
 * was the third choice under "Stop email and calls" and "Remove the contact
 * details given"; on its own, in a list or a detail panel, it says nothing.
 */
const VALUE_WORDS: Record<string, Record<string, string>> = {
  'suppression.action': {
    stop_outreach: 'Stop emails and calls',
    remove_contact: 'Remove their details',
    both: 'Stop contact and remove details',
  },
};

export function makeLookup(meta: MetaResponse): Lookup {
  const studies = new Map(meta.studies.map((s) => [s.key, s]));
  const topics = new Map(meta.topics.map((t) => [t.key, t.label]));
  const fieldLabels = meta.fieldLabels ?? {};
  const fieldEnums = meta.fieldEnums ?? {};
  const order = new Map(Object.keys(fieldLabels).map((k, i) => [k, i]));
  return {
    studyLabel: (k) => (k ? (studies.get(k as never)?.label ?? '—') : '—'),
    studyShort: (k) => (k ? (studies.get(k as never)?.short ?? '—') : '—'),
    topicLabel: (k) => (k ? (topics.get(k as never) ?? '—') : '—'),
    fieldLabel: (k) => fieldLabels[k] ?? k,
    valueLabel: (k, v) => VALUE_WORDS[k]?.[v] ?? fieldEnums[k]?.find((o) => o.key === v)?.label ?? v,
    fieldRank: (k) => order.get(k) ?? Number.MAX_SAFE_INTEGER,
  };
}

/* ── The registry forms ─────────────────────────────────────────────────────
 *
 * The nine forms added with site push bd1db6d store
 * `{ form: "<spec id>", fields: { "<canonical CRM key>": value } }` rather
 * than a bespoke payload, and `/meta` serves the key → label map and the
 * closed vocabularies. Everything below renders that shape generically, so a
 * tenth form needs no dashboard change at all.
 */

export const isRegistryShape = (d: AnyData): boolean =>
  !!d.fields && typeof d.fields === 'object';

/** The `fields` object of a registry-shaped row, or null for the bespoke forms. */
export function registryFieldsOf(r: SubmissionRow): Record<string, unknown> | null {
  const d = r.data as AnyData;
  return isRegistryShape(d) ? (d.fields as Record<string, unknown>) : null;
}

/** Identity keys pulled into the Contact group and the header strip. */
const REGISTRY_IDENTITY = [
  'person.full_name',
  'clinician.name',
  'person.email',
  'clinician.email',
  'person.phone',
  'person.contact',
  'person.org',
  'clinician.org',
  'person.role_title',
] as const;

/** Free-text keys that read as paragraphs, not rows. */
export const REGISTRY_LONG = new Set([
  'inquiry.message',
  'inquiry.note',
  'inquiry.note_oud',
  'fit.sketch',
]);

/** The one answer that summarises each form, first match wins. */
const REGISTRY_HEADLINE = [
  'inquiry.topic',
  'training.track',
  'fit.kind',
  'ping.need',
  'suppression.action',
  'pref.channel',
  'media.outlet',
  'interest.topics[]',
  'routing.interest[]',
] as const;

/** Study-slug fields whose values resolve through the study registry. */
const STUDY_VALUED = new Set(['study.slug', 'interest.topics[]']);

/**
 * One stored answer as the words the form showed. `short` swaps a study's full
 * title for its short name — a table cell has no room for "Open-Label
 * Psilocybin Study in a Transdiagnostic Population"; a detail panel does.
 */
export function registryValue(
  key: string,
  value: unknown,
  L: Lookup,
  opts: { short?: boolean } = {},
): string {
  const study = (v: unknown) => (opts.short ? L.studyShort(String(v)) : L.studyLabel(String(v)));
  if (Array.isArray(value)) {
    const parts = value.map((v) => (STUDY_VALUED.has(key) ? study(v) : L.valueLabel(key, String(v))));
    return parts.length ? parts.join(', ') : '—';
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === '' || value == null) return '—';
  if (STUDY_VALUED.has(key)) return study(value);
  return L.valueLabel(key, String(value));
}

function registryHeadline(d: AnyData, L: Lookup): string {
  const fields = (d.fields ?? {}) as AnyData;
  for (const key of REGISTRY_HEADLINE) {
    if (fields[key] != null && fields[key] !== '') return registryValue(key, fields[key], L);
  }
  return '';
}

const dash = (v: unknown): string => (v === '' || v == null ? '—' : String(v));
const yn = (v: unknown): string => (v ? 'Yes' : 'No');

/** One-line summary used on the "All" tab. */
export function summaryOf(r: SubmissionRow, L: Lookup): string {
  const d = r.data as AnyData;
  if (r.form === 'intake') return `${dash(d.primaryCondition)} · ${L.studyShort(d.study)}`;
  if (r.form === 'referral') {
    const last = String(d.patient?.lastName ?? '');
    return `Patient ${d.patient?.firstName ?? '—'} ${last ? `${last.charAt(0)}.` : ''} · ${dash(d.primaryCondition)}`;
  }
  if (r.form === 'waitlist') {
    const n = (d.studies ?? []).length;
    return `${n} ${n === 1 ? 'study' : 'studies'} · ${(d.studies ?? []).map(L.studyShort).join(', ')}`;
  }
  if (isRegistryShape(d)) {
    const fields = d.fields as AnyData;
    const head = registryHeadline(d, L);
    const study = fields['study.slug'] ? L.studyShort(String(fields['study.slug'])) : '';
    return [study, head].filter(Boolean).join(' · ') || '—';
  }
  const msg = String(d.message ?? '');
  return `${L.topicLabel(d.topic)} — ${msg.slice(0, 70)}${msg.length > 70 ? '…' : ''}`;
}

export interface DetailGroup {
  title: string;
  fields: Array<{ k: string; v: string }>;
  long: Array<{ k: string; v: string }>;
}

const f = (k: string, v: unknown) => ({ k, v: dash(v) });

/**
 * How old the person is — which the site stopped asking as a birth date in
 * September 2026.
 *
 * Three shapes reach this function and all three have to render:
 *
 *   `ageIs90Plus: true`  the HIPAA Safe Harbor aggregate; `age` is null
 *   `age: 42`            every intake since the change
 *   `dob: "1971-03-14"`  every intake before it, still in the table
 *
 * Old rows keep their own label, because relabelling a stored birth date as
 * "Age" would be describing the record as something it is not. A redacted row
 * hands back a marker rather than a date, so the ISO shape is checked before
 * the value is treated as one.
 */
function ageField(d: AnyData): { k: string; v: string } {
  if (d.ageIs90Plus === true) return f('Age', '90 or older');
  if (typeof d.age === 'number') return f('Age', `${d.age}`);

  const dob = typeof d.dob === 'string' ? d.dob : '';
  if (/^\d{4}-\d{2}-\d{2}/.test(dob)) {
    const years = ageFrom(dob);
    return f('Date of birth', `${plainDate(dob)}${years === null ? '' : ` · ${years}y`}`);
  }
  // Redacted, or simply absent: show whichever field the row carries.
  return dob ? f('Date of birth', dob) : f('Age', d.age);
}

/** Grouped detail fields, ported from the demo's `groupsOf`. */
export function groupsOf(r: SubmissionRow, L: Lookup): DetailGroup[] {
  const d = r.data as AnyData;

  if (r.form === 'intake') {
    const groups: DetailGroup[] = [
      {
        title: 'Contact',
        fields: [
          f('Pronouns', d.pronouns),
          ageField(d),
          f('Phone', d.phone),
          f('City / State', `${dash(d.city)}, ${dash(d.state)}`),
          f('How they heard about ENACT', d.heardAbout),
        ],
        long: [],
      },
      {
        title: 'Study',
        fields: [
          f('Study requested', L.studyLabel(d.study)),
          f('Primary condition', d.primaryCondition),
          f('Other conditions', (d.otherConditions ?? []).length ? d.otherConditions.join(', ') : 'None'),
          f('Consent acknowledged', yn(d.consent)),
        ],
        long: [f('Anything else they wanted us to know', d.notes)],
      },
    ];

    // The chosen study's own questions (the P2/P3/P4 sections of the public
    // form) ride along as `studyAnswers`, keyed by canonical CRM key exactly
    // like the registry forms, so they read through the same vocabulary.
    // Absent entirely when the person picked "not sure yet".
    const answers = (d.studyAnswers ?? {}) as Record<string, unknown>;
    const keys = Object.keys(answers).sort((a, b) => L.fieldRank(a) - L.fieldRank(b));
    if (keys.length) {
      const row = (k: string) => ({ k: L.fieldLabel(k), v: registryValue(k, answers[k], L) });
      groups.push({
        title: 'Study questions',
        fields: keys.filter((k) => !REGISTRY_LONG.has(k)).map(row),
        long: keys.filter((k) => REGISTRY_LONG.has(k)).map(row),
      });
    }
    return groups;
  }

  if (r.form === 'referral') {
    const c = d.clinician ?? {};
    const p = d.patient ?? {};
    return [
      {
        title: 'About the referring clinician',
        fields: [
          f('Credentials / role', c.credentials),
          f('Practice / organization', c.practice),
          f('Email', c.email),
          f('Phone', c.phone),
        ],
        long: [],
      },
      {
        title: 'About the patient',
        fields: [
          f('Patient name', `${p.firstName ?? '—'} ${p.lastName ?? ''}`.trim()),
          f('Patient phone or email', p.contact),
          f('Primary condition', d.primaryCondition),
          f('Study referred for', d.study ? L.studyLabel(d.study) : 'Not specified'),
          f('Patient permission confirmed', yn(d.permission)),
        ],
        long: [f('Brief clinical summary', d.clinicalSummary)],
      },
    ];
  }

  if (r.form === 'waitlist') {
    return [
      {
        title: 'Contact',
        fields: [
          f('Email', d.email),
          f('Phone', d.phone),
          f('Acknowledged no guarantee of enrollment', yn(d.acknowledged)),
        ],
        long: [],
      },
      {
        title: 'Studies requested',
        fields: (d.studies ?? []).map((k: string) => f(L.studyLabel(k), L.studyShort(k))),
        long: [f('Why they want to join', d.reason)],
      },
    ];
  }

  if (isRegistryShape(d)) {
    const fields = d.fields as AnyData;
    const identity: Array<{ k: string; v: string }> = [];
    const rest: Array<{ k: string; v: string }> = [];
    const long: Array<{ k: string; v: string }> = [];

    for (const [key, value] of Object.entries(fields)) {
      // Provenance riders, not answers.
      if (key === 'routing.stage' || key === 'routing.coordinator') continue;
      const row = { k: L.fieldLabel(key), v: registryValue(key, value, L) };
      if (REGISTRY_LONG.has(key)) long.push(row);
      else if ((REGISTRY_IDENTITY as readonly string[]).includes(key)) identity.push(row);
      else rest.push(row);
    }

    // MySQL's JSON column does not preserve key order, so impose one: the
    // person's name leads, then how to reach them, then where they work.
    const rank = (row: { k: string }) =>
      REGISTRY_IDENTITY.findIndex((k) => L.fieldLabel(k) === row.k);
    identity.sort((a, b) => rank(a) - rank(b));

    const groups: DetailGroup[] = [];
    if (identity.length) groups.push({ title: 'Contact', fields: identity, long: [] });
    groups.push({ title: 'Request', fields: rest, long });
    return groups;
  }

  return [
    {
      title: 'Details',
      fields: [f('Organization', d.organization), f('Topic', L.topicLabel(d.topic))],
      long: [f('Message', d.message)],
    },
  ];
}

/**
 * The identifiers a clinic banner carries above the record.
 *
 * These are the handful of fields staff read to be sure they have the right
 * person in front of them, so they belong pinned to the header rather than
 * buried three sections down. The keys are deliberately the same strings
 * `groupsOf` uses, so a caller that shows this strip can drop the duplicates
 * out of the groups by label alone.
 */
export function identityOf(r: SubmissionRow, L: Lookup): Array<{ k: string; v: string }> {
  const d = r.data as AnyData;

  if (r.form === 'intake') {
    return [
      ageField(d),
      f('Pronouns', d.pronouns),
      f('Phone', d.phone),
      f('City / State', d.city || d.state ? `${dash(d.city)}, ${dash(d.state)}` : ''),
    ];
  }

  if (r.form === 'referral') {
    const p = d.patient ?? {};
    const name = `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();
    return [
      f('Patient name', name),
      f('Patient phone or email', p.contact),
      f('Primary condition', d.primaryCondition),
    ];
  }

  if (r.form === 'waitlist') return [f('Email', d.email), f('Phone', d.phone)];

  if (isRegistryShape(d)) {
    const fields = d.fields as AnyData;
    return REGISTRY_IDENTITY.filter((k) => fields[k]).map((k) =>
      f(L.fieldLabel(k), registryValue(k, fields[k], L)),
    );
  }

  return [f('Organization', d.organization), f('Topic', L.topicLabel(d.topic))];
}

/** The second summary tile in the drawer. */
export function tile2(r: SubmissionRow, L: Lookup): { label: string; value: string } {
  const d = r.data as AnyData;
  if (r.form === 'intake') return { label: 'Study', value: L.studyLabel(d.study) };
  if (r.form === 'referral')
    return { label: 'Study', value: d.study ? L.studyLabel(d.study) : 'Not specified' };
  if (r.form === 'waitlist')
    return { label: 'Studies', value: (d.studies ?? []).map(L.studyShort).join(', ') };
  if (isRegistryShape(d)) {
    const fields = d.fields as AnyData;
    if (fields['study.slug'])
      return { label: 'Study', value: L.studyLabel(String(fields['study.slug'])) };
    return { label: 'About', value: registryHeadline(d, L) || '—' };
  }
  return { label: 'Topic', value: L.topicLabel(d.topic) };
}
